import { useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import { confirmDialog } from "./Confirm";

// ── THE ELECTION CALENDAR (0413) ─────────────────────────────────────────────────────────────────
//
// Caleb, 2026-10-31: an already-voted list belongs to an election DAY, and counts only for the
// campaigns whose election is that day. This is every day Orator knows:
//   * the regular days the Election Code fixes, with early voting, filled in from the law
//     (backend/elections/calendar.py) -- they cannot be moved or removed here;
//   * the days the law does not fix, added here: a special election, or a city or school runoff.
// "Campaigns on it" is who is in that election now -- the one a manager chose, or the next on the
// campaign's path. A day a campaign adds for itself is shown here, marked, and is that campaign's
// alone: nobody reviews it and it is never offered to anyone else (Caleb, 2026-10-31: "we just let
// the campaigns change the election dates themselves"). A day added HERE is offered to every campaign
// where it is held.

type Election = {
  id: string; day: string; kind: string; label: string; for_whom: string | null;
  early_start: string | null; early_end: string | null; fips: number[] | null; counties: string[];
  source: "law" | "staff" | "campaign"; status: "approved" | "pending";
  added_for_name: string | null; added_by: string | null; passed: boolean; campaigns: string[];
};
type County = { fips: number; name: string };
type Form = { id?: string; source?: string; kind: string; label: string; day: string; early_start: string;
              early_end: string; fips: number[]; for_whom: string };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** A calendar day as written, never through Date() -- "2026-11-03" is midnight UTC, the 2nd in Texas. */
function day(iso: string | null, year = true): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}${year ? `, ${m[1]}` : ""}` : "—";
}
function plain(e: any): string {
  const m = String(e?.message ?? e).replace(/^API \d+: /, "");
  if (/failed to fetch|networkerror|load failed/i.test(m)) return "The server didn't answer. Try again in a minute.";
  try { const j = JSON.parse(m); if (j?.detail) return String(j.detail); } catch { /* not JSON */ }
  return m;
}
const BLANK: Form = { kind: "special", label: "", day: "", early_start: "", early_end: "", fips: [], for_whom: "" };

export function Elections() {
  const [rows, setRows] = useState<Election[] | null>(null);
  const [counties, setCounties] = useState<County[]>([]);
  const [today, setToday] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  const [year, setYear] = useState<string>("");
  const [form, setForm] = useState<Form | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setErr(null);
    api<{ elections: Election[]; counties: County[]; today: string }>("/elections")
      .then((r) => {
        setRows(r.elections); setCounties(r.counties); setToday(r.today);
        setYear((y) => y || r.today.slice(0, 4));
      })
      .catch((e) => setErr(plain(e)));
  };
  useEffect(load, []);

  const years = useMemo(() => Array.from(new Set((rows ?? []).map((r) => r.day.slice(0, 4)))).sort(), [rows]);
  const shown = (rows ?? []).filter((r) => r.day.startsWith(year));
  // THE ONE UNDER WAY: the soonest day not yet passed. Said in the row, so "Now" means something.
  const now = (rows ?? []).find((r) => !r.passed)?.day;
  const countyName = (f: number) => counties.find((c) => c.fips === f)?.name ?? String(f);

  function edit(r: Election) {
    setFormErr(null);
    setForm({ id: r.id, source: r.source, kind: r.kind, label: r.label, day: r.day, early_start: r.early_start ?? "",
              early_end: r.early_end ?? "", fips: r.fips ?? [], for_whom: r.for_whom ?? "" });
  }

  async function save() {
    if (!form) return;
    if (!form.label.trim() || !form.day) { setFormErr("Give it a name and an Election Day."); return; }
    setSaving(true); setFormErr(null);
    const body = JSON.stringify({ kind: form.kind, label: form.label, day: form.day, for_whom: form.for_whom || null,
      early_start: form.early_start || null, early_end: form.early_end || null, fips: form.fips });
    try {
      await api(form.id ? `/elections/${form.id}/fix` : "/elections", { method: "POST", body });
      setForm(null); load();
    } catch (e: any) { setFormErr(plain(e)); }
    setSaving(false);
  }

  async function remove(r: Election) {
    const ok = await confirmDialog(
      `Take "${r.label}" (${day(r.day)}) off the calendar?\n\nA campaign that chose it goes back to the next election on its path, and any already-voted list filed under it stops counting for them.`,
      { danger: true, confirmLabel: "Take it off" });
    if (!ok) return;
    try { await api(`/elections/${r.id}/remove`, { method: "POST" }); setForm(null); load(); }
    catch (e: any) { setErr(plain(e)); }
  }

  const law = form?.source === "law";
  return (
    <div className="wrap">
      <div className="row" style={{ marginBottom: 6, flexWrap: "wrap", alignItems: "baseline", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Election calendar</h2>
        <span className="muted" style={{ fontSize: 13 }}>Texas · every election day Orator knows. An already-voted list counts only for the campaigns on its day.</span>
        <button className="btn accent" style={{ marginLeft: "auto" }} onClick={() => { setFormErr(null); setForm({ ...BLANK }); }}>＋ Special election</button>
      </div>

      {form && (
        <div className="card" style={{ padding: 16, marginTop: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 10 }}>{form.id ? (law ? "Fix early voting" : "Fix this election") : "Add an election the law doesn't set"}</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            {!law && (
              <label style={{ fontSize: 13 }}>
                <div className="muted" style={{ marginBottom: 4 }}>Kind</div>
                <select className="ctl" value={form.kind} disabled={!!form.id} onChange={(e) => setForm({ ...form, kind: e.target.value })} style={{ width: 200 }}>
                  <option value="special">Special election</option>
                  <option value="local_runoff">City or school runoff</option>
                </select>
              </label>
            )}
            <label style={{ fontSize: 13, flex: "1 1 260px" }}>
              <div className="muted" style={{ marginBottom: 4 }}>Name</div>
              <input className="ctl" value={form.label} maxLength={120} placeholder="Special election, City Council District 8"
                onChange={(e) => setForm({ ...form, label: e.target.value })} style={{ width: "100%" }} />
            </label>
            <label style={{ fontSize: 13 }}>
              <div className="muted" style={{ marginBottom: 4 }}>Election Day</div>
              <input className="ctl" type="date" value={form.day} disabled={law} onChange={(e) => setForm({ ...form, day: e.target.value })} style={{ width: 170 }} />
            </label>
            <label style={{ fontSize: 13 }}>
              <div className="muted" style={{ marginBottom: 4 }}>Early voting from</div>
              <input className="ctl" type="date" value={form.early_start} onChange={(e) => setForm({ ...form, early_start: e.target.value })} style={{ width: 170 }} />
            </label>
            <label style={{ fontSize: 13 }}>
              <div className="muted" style={{ marginBottom: 4 }}>to</div>
              <input className="ctl" type="date" value={form.early_end} onChange={(e) => setForm({ ...form, early_end: e.target.value })} style={{ width: 170 }} />
            </label>
          </div>
          {!law && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start", marginTop: 12 }}>
              <div style={{ fontSize: 13, flex: "1 1 260px" }}>
                <div className="muted" style={{ marginBottom: 4 }}>Where it's held</div>
                <select className="ctl" value="" onChange={(e) => {
                  const f = Number(e.target.value);
                  if (f && !form.fips.includes(f)) setForm({ ...form, fips: [...form.fips, f] });
                }} style={{ width: 260 }}>
                  <option value="">{form.fips.length ? "Add another county…" : "Every county (statewide)"}</option>
                  {counties.filter((c) => !form.fips.includes(c.fips)).map((c) => <option key={c.fips} value={c.fips}>{c.name}</option>)}
                </select>
                {form.fips.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {form.fips.map((f) => (
                      <div key={f} className="row" style={{ gap: 8 }}>
                        <span>{countyName(f)} County</span>
                        <button type="button" className="btn sm" title="Remove this county" onClick={() => setForm({ ...form, fips: form.fips.filter((x) => x !== f) })}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <label style={{ fontSize: 13, flex: "1 1 260px" }}>
                <div className="muted" style={{ marginBottom: 4 }}>Who it's for</div>
                <input className="ctl" value={form.for_whom} maxLength={200} placeholder="City Council District 8"
                  onChange={(e) => setForm({ ...form, for_whom: e.target.value })} style={{ width: "100%" }} />
              </label>
            </div>
          )}
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Leave both early voting dates blank to use the law's: from the 17th day before (the next business day if that's a weekend or holiday) to the 4th day before.
          </div>
          {formErr && <div className="err" style={{ marginTop: 8 }}>{formErr}</div>}
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
            {form.id && !law && <button className="btn warn" onClick={() => { const r = rows?.find((x) => x.id === form.id); if (r) remove(r); }} disabled={saving}>Take it off the calendar</button>}
            <button className="btn accent" style={{ marginLeft: "auto" }} onClick={save} disabled={saving}>{saving ? "Saving…" : form.id ? "Save" : "Add it"}</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 16, marginTop: 16 }}>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          {years.map((y) => <button key={y} className={"btn sm" + (y === year ? " accent" : "")} onClick={() => setYear(y)}>{y}</button>)}
        </div>
        {err && <div className="err" style={{ marginTop: 10 }}>Couldn't load the calendar. {err}</div>}
        {!err && !rows && <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>Loading…</div>}
        {!err && rows && shown.length === 0 && <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>No elections in {year}.</div>}
        {!err && rows && shown.length > 0 && (
          <table style={{ marginTop: 10 }}>
            <thead><tr><th>Election Day</th><th>Election</th><th>Early voting</th><th>Who it's for</th><th>Campaigns on it</th><th></th></tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} style={{ opacity: r.passed ? 0.55 : 1, verticalAlign: "top" }}>
                  <td style={{ whiteSpace: "nowrap", fontWeight: 700 }}>{day(r.day)}</td>
                  <td>
                    {r.label}
                    <div className="muted" style={{ fontSize: 12 }}>
                      {r.source === "law" ? "Set by law" : r.source === "campaign" ? `Added by ${r.added_for_name ?? "a campaign"} · that campaign only` : `Added by ${r.added_by ?? "Orator"}`}
                    </div>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{r.early_start ? `${day(r.early_start, false)} – ${day(r.early_end, false)}` : "—"}</td>
                  <td>
                    {r.for_whom || "—"}
                    {r.counties.length > 0 && <div className="muted" style={{ fontSize: 12 }}>{r.counties.join(", ")} {r.counties.length === 1 ? "County" : "Counties"}</div>}
                  </td>
                  <td>
                    {r.campaigns.length === 0 ? <span className="muted">{r.passed ? "—" : "0"}</span>
                      : <span title={r.campaigns.join("\n")}>{r.campaigns.length} · <span className="muted" style={{ fontSize: 12 }}>{r.campaigns.slice(0, 3).join(", ")}{r.campaigns.length > 3 ? "…" : ""}</span></span>}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {r.passed ? <span className="muted">Passed</span>
                      : r.source === "campaign" ? <span className="muted">Theirs to change</span>
                      : (<>
                        {r.day === now && <span style={{ color: "var(--good)", fontWeight: 700, marginRight: 8 }}>Now</span>}
                        <button className="btn sm" onClick={() => edit(r)}>Fix</button>
                      </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
          Regular days follow the Election Code and are checked each year against the Secretary of State's published dates{today ? ` (today is ${day(today)})` : ""}.
          A special added here is offered to every campaign licensed where it's held.
        </div>
      </div>
    </div>
  );
}
